import { useEffect, useMemo, useRef, useState } from "react";
import {
  faBook,
  faChevronLeft,
  faChevronRight,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../../../Components/Common/Button";
import Card from "../../../Components/Common/Card";
import HeaderArrow from "../../../Elements/Images/Svgs/HeaderArrow";
import HeaderPattern from "../../../Elements/Images/Svgs/HeaderPattern";
import Pencil from "../../../Elements/Images/Svgs/Pencil";
import CivilScale from "../../../Elements/Images/Svgs/CivilScale";
import CivilEngr from "../../../Elements/Images/Svgs/CivilEngr";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Marquee from "react-fast-marquee";
import { useNavigate } from "react-router-dom";
import { publicUrls } from "../../../Api/Auth/AppUrls";
import { motion } from "framer-motion";
import { useAuth } from "../../../Context/AuthContext";

const slides = [
  {
    title: "Online E-Learning Courses",
    header: "Welcome to Road to FE",
    description:
      "Road to FE's top-rated engineering, project management, & architecture courses have provided tens of thousands of professionals with the tools to pass their exams and advance their careers.",
    image:
      "https://getmasum.com/themes-wp/edumoon/wp-content/uploads/2024/04/banner.png",
  },
  {
    title: "Master Engineering & Management",
    header: "Welcome to Road to FE",
    description:
      "Gain expertise in engineering and project management with our professional courses designed for success.",
    image:
      "https://getmasum.com/themes-wp/edumoon/wp-content/uploads/2024/04/banner.png",
  },
  {
    title: "Achieve Your Career Goals",
    header: "Welcome to Road to FE",
    description:
      "Get access to industry-leading courses and enhance your career with our specialized programs.",
    image:
      "https://getmasum.com/themes-wp/edumoon/wp-content/uploads/2024/04/banner.png",
  },
];

export default function HeroSection({ data }) {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [contentData, setContentData] = useState(slides || []);
  const [marqueeContent, setMarqueeContent] = useState("");
  const navigate = useNavigate();
  const { categoryData } = useAuth();

  // Generate course subitems dynamically based on visible categories
  const courseSubItems = useMemo(() => {
    if (!categoryData) return [];

    const categoryMap = {
      Civil: { link: publicUrls.CivilCourses },
      Mechanical: { link: publicUrls.MechanicalCourses },
    };

    return categoryData
      .filter((category) => category.visibility)
      .map((category) => ({
        label: category.visibleTitle || category.title,
        link: categoryMap[category.title]?.link || "#",
      }));
  }, [categoryData]);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (data?.data) {
      const content = JSON.parse(data?.data?.content);      
      if (content) {
        const updatedCont = [
          {
            title: content.title1 || slides[0].title,
            header: content.header1 || slides[0].header,
            description: content.subTitle1 || slides[0].description,
            image: content.image1 || slides[0].image,
          },
          {
            title: content.title2 || slides[1].title,
            header: content.header2 || slides[1].header,
            description: content.subTitle2 || slides[1].description,
            image: content.image2 || slides[1].image,
          },
          {
            title: content.title3 || slides[2].title,
            header: content.header3 || slides[2].header,
            description: content.subTitle3 || slides[2].description,
            image: content.image3 || slides[2].image,
          },
        ];
        setContentData(updatedCont);
        setMarqueeContent(
          content?.marquee || "join Us Now! Exciting new materials!"
        );
      }
    }
  }, [data?.data]);

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    setMouseX((clientX / window.innerWidth - 0.6) * 8); // Reduced intensity
    setMouseY((clientY / window.innerHeight - 0.6) * 8);
  };

  const handleNext = () => {
    setCurrentSlide((prev) => (prev + 1) % contentData.length);
  };

  const handlePrev = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + contentData.length) % contentData.length
    );
  };

  return (
    <>
      <Marquee autoFill gradient className="py-2 overflow-hidden text-sm sm:text-base">
        <span
          className="mr-8 sm:mr-16"
          dangerouslySetInnerHTML={{ __html: marqueeContent || "" }}
        />
      </Marquee>

      <div
        className="relative bg-primary text-white overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        <motion.div
          initial={{ opacity: 1, y: -40 }} // Start above the view
          whileInView={{ opacity: 1, y: 0 }} // End at normal position
          transition={{ duration: 1, ease: "easeOut" }} // Longer transition for more emphasis
        >
          {/* Left Clickable Button - Prev */}
          <button
            className="hidden md:block absolute top-1/2 left-6 z-10 transform -translate-y-1/2 cursor-pointer"
            onClick={handlePrev}
          >
            <span className="text-white bg-black/50 px-4 py-2 rounded-full">
              <FontAwesomeIcon icon={faChevronLeft} />
            </span>
          </button>

          {/* Right Clickable Button - Next */}
          <button
            className="hidden md:block absolute top-1/2 right-6 z-10 transform -translate-y-1/2 cursor-pointer"
            onClick={handleNext}
          >
            <span className="text-white bg-black/50 px-4 py-2 rounded-full">
              <FontAwesomeIcon icon={faChevronRight} />
            </span>
          </button>

          {/* Pan Effect Wrapper */}
          <section className="relative py-12 sm:py-16 px-4 sm:px-6 lg:px-20 flex flex-col lg:flex-row items-center justify-between transition-transform duration-300 gap-8">
            {/* Background Header Arrow */}
            <div className="hidden lg:flex absolute top-96 left-36 w-1/2 justify-center">
              <HeaderArrow />
            </div>

            {/* Left Content */}
            <div className="lg:w-1/2 w-full lg:text-left space-y-4 sm:space-y-6">
              <h3 className="text-lg sm:text-xl font-semibold text-white text-center">
                <span
                  className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
                  dangerouslySetInnerHTML={{
                    __html: contentData[currentSlide]?.header || "",
                  }}
                />
              </h3>
              {/* <h1 className="text-4xl lg:text-5xl font-bold">
                <span className="text-[#0D5FF9]">Welcome</span> to Road to PE
              </h1> */}
              <p
                className="text-base sm:text-lg text-base/8 px-4"
                dangerouslySetInnerHTML={{
                  __html: contentData[currentSlide]?.description || "",
                }}
              />

              {/* Buttons */}
              <div className="flex justify-center lg:justify-start gap-3 sm:gap-4 mt-6 w-full sm:w-auto">
                {courseSubItems.length > 0 && (
                  <div className="relative inline-block" ref={dropdownRef}>
                    <Button
                      type="primaryNoHover"
                      className="!bg-[#0D5FF9]"
                      onClick={() => setIsDropdownOpen((prev) => !prev)}
                    >
                      All Courses
                    </Button>

                    {isDropdownOpen && (
                      <div className="w-full absolute mt-2 bg-white border rounded shadow-lg z-10">
                        {courseSubItems.map((item, index) => (
                          <button
                            key={index}
                            className="w-full text-left px-4 py-2 hover:bg-gray-100 text-primary"
                            onClick={() => {
                              navigate(item.link);
                              setIsDropdownOpen(false);
                            }}
                          >
                            {item.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <Button
                  onClick={() => navigate(publicUrls.Contact)}
                  className="border border-gray-300 text-white px-6 py-3 rounded-full hover:bg-gray-800 transition"
                >
                  Contact Us
                </Button>
              </div>

              {/* Social Proof */}
              <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mt-4">
                <div className="flex -space-x-3">
                  <img
                    src={
                      "https://storage.googleapis.com/road-to-fe/uploads/1741110068604-0bcf572c-2c60-4ba1-99b0-7e9c6bf71d3a-person1.webp"
                    }
                    alt="User1"
                    className="w-14 h-14 rounded-full border-2 border-white object-cover"
                  />
                  <img
                    src={
                      "https://storage.googleapis.com/road-to-fe/uploads/1741110088710-42ea2000-ded3-4849-976f-243c8ddb523d-person2.webp"
                    }
                    alt="User2"
                    className="w-14 h-14 rounded-full border-2 border-white object-cover"
                  />
                  <img
                    src={
                      "https://storage.googleapis.com/road-to-fe/uploads/1741110092837-af81c820-2ba7-4255-bd8b-ee5acd01ecf1-person3.webp"
                    }
                    alt="User3"
                    className="w-14 h-14 rounded-full border-2 border-white object-cover"
                  />
                </div>
                <span>Student Care - Our Top Priority</span>
              </div>
            </div>

            {/* Right Side Image */}
            <div className="lg:w-1/3 w-full relative mt-6 sm:mt-10 lg:mt-0 flex flex-col items-center">
              <img
                src={contentData[currentSlide]?.image}
                alt="Hero"
                className="rounded-3xl w-full max-w-sm sm:max-w-md lg:w-[500px] object-cover"
              />

              {/* Stats */}
              <div
                className="hidden md:block absolute top-10 -left-5"
                style={{ transform: `translate(${mouseX}px, ${mouseY}px)` }}
              >
                <Card
                  icon={faUser}
                  title={"800"}
                  subtitle="Practice Problems"
                />
              </div>
              <div
                className="hidden md:block absolute top-24 -right-4"
                style={{ transform: `translate(${mouseX}px, ${mouseY}px)` }}
              >
                <Card icon={faUser} title={"2"} subtitle="Model Tests" />
              </div>

              <div
                className="hidden md:block absolute bottom-0 left-1/2 -mb-10"
                style={{ transform: `translate(${mouseX}px, ${mouseY}px)` }}
              >
                <Card icon={faBook} title={"75 hrs"} subtitle="Class Lecture" />
              </div>

              {/* Mobile stats */}
              <div className="grid grid-cols-1 min-[420px]:grid-cols-3 gap-3 w-full mt-6 md:hidden">
                <Card icon={faUser} title={"800"} subtitle="Practice Problems" />
                <Card icon={faUser} title={"2"} subtitle="Model Tests" />
                <Card icon={faBook} title={"75 hrs"} subtitle="Class Lecture" />
              </div>
            </div>

            {/* SVG Background Patterns */}
            <div
              className={`hidden lg:block absolute bottom-[-160px] left-0 w-48 transition-transform duration-300 transform`}
            >
              <HeaderPattern />
            </div>
            <div
              className={`hidden lg:block absolute top-[-160px] left-0 w-48 transition-transform duration-300 transform`}
            >
              <HeaderPattern />
            </div>
            <div
              className={`hidden sm:block absolute top-48 left-3 w-12`}
              style={{ transform: `translate(${mouseX}px, ${mouseY}px)` }}
            >
              <Pencil />
            </div>
            <div
              className={`hidden sm:block absolute top-48 right-[40%] w-16`}
              style={{ transform: `translate(${mouseX}px, ${mouseY}px)` }}
            >
              <CivilScale />
            </div>
            <div
              className={`hidden sm:block absolute bottom-12 right-[40%] w-24`}
              style={{ transform: `translate(${mouseX}px, ${mouseY}px)` }}
            >
              <CivilEngr />
            </div>
            <div
              className={`hidden lg:block absolute top-[-160px] right-0 w-48 rotate-180 transition-transform duration-300 transform`}
            >
              <HeaderPattern />
            </div>
            <div
              className={`hidden lg:block absolute bottom-[-160px] right-0 w-48 rotate-180 transition-transform duration-300 transform`}
            >
              <HeaderPattern />
            </div>
          </section>
        </motion.div>
      </div>

      {/* Mobile navigation */}
      <div className="md:hidden flex items-center justify-center gap-4 py-6 bg-primary/90">
        <button
          className="w-10 h-10 rounded-full border border-white/40 flex items-center justify-center"
          onClick={handlePrev}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="text-white" />
        </button>
        <button
          className="w-10 h-10 rounded-full border border-white/40 flex items-center justify-center"
          onClick={handleNext}
        >
          <FontAwesomeIcon icon={faChevronRight} className="text-white" />
        </button>
      </div>
    </>
  );
}
